import { equals } from 'ramda';

import {
  getDateRangeValue,
  getTermsValue,
  isSuburban,
  JsonQuery,
  offerTypeFromJsonQuery,
  TJsonQuery,
} from '../../../packages/JsonQuery';
import { setEarlyAccessData } from '../earlyAccess';
import { setEtazhiBlackFridayData } from '../etazhiBlackFriday';
import { resetJournalHighlights } from '../journal';
import { setLoading } from './setLoading';
import { FILTERS_SEARCH_SEARCH_REQUEST_FINISHED } from './types';
import { incrementAnimationCounter } from '../consultantCianChat';
import { Dispatch } from 'shared/common/state';
import { trackApplyDatesModal } from 'shared/containers/FiltersContainer/tracking/trackApplyDatesModal';
import { EObjectType, IJsonQueryDeveloper } from 'shared/json_query';
import { fetchDeveloperInfoSafe } from 'shared/services/fetchDeveloperInfo';
import { fetchEarlyAccessDataIfNeeded } from 'shared/services/fetchEarlyAccessData';
import { fetchEtazhiBlackFridayDataIfNeeded } from 'shared/services/fetchEtazhiBlackFridayDataIfNeeded';
import { fetchNewobjectsBuilderIfNeeded } from 'shared/services/fetchNewobjectsBuilder';
import { fetchOffers, prepareJsonQuery } from 'shared/services/fetchOffers';
import { fetchQuickLinksSafe } from 'shared/services/fetchQuickLinks';
import { fetchRecommendedVillagesSafe } from 'shared/services/fetchRecommendedVillages';
import { fetchSimilarNewobjectsIfNeeded } from 'shared/services/fetchSimilarNewobjects';
import { fetchSuburbanBuildersProjectsSafe } from 'shared/services/fetchSuburbanBuildersProjects';
import { fetchVillageInfoSafe } from 'shared/services/fetchVillageInfo';
import { IDeveloperInfo } from 'shared/types/developerInfo';
import { TLocation } from 'shared/types/location';
import { IOffersData } from 'shared/types/offersData';
import { IRecommendedVillages } from 'shared/types/recommendedVillages';
import { TThunkAction } from 'shared/types/redux';
import { ISearchMeta } from 'shared/types/searchMeta';
import { ISuburbanProjectSuggest } from 'shared/types/suburbanBuildersProjects';
import { IVillageInfo } from 'shared/types/villageInfo';
import { cutDescriptionFromOffers } from 'shared/utils';
import { displayConsultantPhone } from 'shared/utils/displayNewbuildingConsultantPhone';
import { getRegionId } from 'shared/utils/geo';
import { saveHistorySearch } from 'shared/utils/history_search';
import { isDeveloperPage } from 'shared/utils/isDeveloperPage';
import { isHybridSearchRedirectNeeded } from 'shared/utils/isHybridSearchRedirectNeeded';
import { isRedirectNeeded } from 'shared/utils/isRedirectNeeded';
import { isSubdomainRedirectNeeded } from 'shared/utils/isSubdomainRedirectNeeded';
import { offersDecorator } from 'shared/utils/offersDecorator';
import { trackPageView } from 'shared/utils/tracking/pageview';

interface IParams {
  jsonQuery: TJsonQuery;
  location: TLocation;
  searchMeta: ISearchMeta;
}

export function makeSearch({ jsonQuery, location, searchMeta }: IParams): TThunkAction<Promise<void>> {
  return async (dispatch: Dispatch, getState, context) => {
    dispatch(setLoading());

    let state = getState();

    if (areDatesChanged(state.results.jsonQuery, jsonQuery)) {
      trackApplyDatesModal();
    }

    const { fullUrl } = searchMeta;
    const { config, journalHighlights, abUseExperiments, currentSubdomain } = state;

    if (
      fullUrl &&
      (isRedirectNeeded(config, jsonQuery, fullUrl) ||
        isSubdomainRedirectNeeded(fullUrl) ||
        isHybridSearchRedirectNeeded({
          jsonQuery,
          experiments: abUseExperiments,
        }))
    ) {
      window.location.assign(fullUrl);

      return;
    }

    const isAdditionalTop3Disabled = config.get('serp.isAdditionalTop3Enabled') !== 'true';

    if (
      window.__reloadHeader__ &&
      jsonQuery.region &&
      !equals(jsonQuery.region.value, state.results.jsonQuery.region?.value)
    ) {
      window.__reloadHeader__();
    }

    const data: IOffersData = await fetchOffers(context, {
      jsonQuery: prepareJsonQuery({ jsonQuery, location }),
    });

    /**
     * Сохранение текущего поиска для истории поисков
     */
    saveHistorySearch(data.seoData.h1, data.fullUrl, data.jsonQuery);

    /**
     * Если текущий путь не чпу, сбрасываем имеющиеся статьи журнала
     */
    if (data.fullUrl.includes('/cat.php') && journalHighlights.length > 0) {
      dispatch(resetJournalHighlights());
    }

    const suggestions = data.suggestOffersSerializedList;

    data.offersSerialized = cutDescriptionFromOffers(data.offersSerialized, state.userAgent);

    if (suggestions) {
      data.suggestOffersSerializedList = cutDescriptionFromOffers(suggestions, state.userAgent);
    }

    // Обработка объявлений
    offersDecorator(data.offersSerialized, isAdditionalTop3Disabled);

    let villageInfo: IVillageInfo | null = null;
    if (data.jsonQuery.kp_id) {
      const oldVillageInfoId = state.results.villageInfo && state.results.villageInfo.id;
      villageInfo =
        oldVillageInfoId === data.jsonQuery.kp_id.value
          ? state.results.villageInfo || null
          : await fetchVillageInfoSafe(context, { id: data.jsonQuery.kp_id.value });
    }

    let developerInfo: IDeveloperInfo | null = null;
    if (data.jsonQuery.geo && isDeveloperPage(data.jsonQuery)) {
      developerInfo = await fetchDeveloperInfoSafe(context, {
        subdomain: currentSubdomain,
        id: (data.jsonQuery.geo.value[0] as IJsonQueryDeveloper).id,
      });
    }

    const [similarNewobjects, similarNewobjectsFromDeveloper, quickLinks, earlyAccessData, etazhiBlackFridayData] =
      await Promise.all([
        fetchSimilarNewobjectsIfNeeded(context, data),
        fetchNewobjectsBuilderIfNeeded(context, data),
        fetchQuickLinksSafe(context, {
          subdomain: currentSubdomain,
          jsonQuery: data.jsonQuery,
        }),
        fetchEarlyAccessDataIfNeeded(context, {
          jsonQuery: data.jsonQuery,
          regionId: getRegionId(location),
        }),

        /**
         * Код появился к проекту "Чёрная пятница" для компании "Этажи".
         * @todo: удалить в задаче CD-250843
         */
        fetchEtazhiBlackFridayDataIfNeeded(context, { getState, jsonQuery: data.jsonQuery }),
      ]);

    dispatch(setEarlyAccessData(earlyAccessData));
    dispatch(setEtazhiBlackFridayData(etazhiBlackFridayData?.offersSerialized ?? null));

    const isSuburbanSearch = isSuburban(offerTypeFromJsonQuery(data.jsonQuery));

    state = getState();

    document.title = data.seoData.title || document.title;
    window.history.pushState({}, document.title, fullUrl);

    const regionId = getRegionId(location);

    const recommendedVillages: IRecommendedVillages | null = isSuburbanSearch
      ? await fetchRecommendedVillagesSafe(context, {
          jsonQuery: new JsonQuery(data.jsonQuery).setRegion([Math.abs(regionId)]).toJSON(),
        })
      : null;

    const objectType = getTermsValue('object_type')(data.jsonQuery);

    const suburbanBuildersProjects: ISuburbanProjectSuggest[] = objectType?.includes(EObjectType.Land)
      ? await fetchSuburbanBuildersProjectsSafe(context, {
          regionId,
        })
      : [];

    dispatch({
      type: FILTERS_SEARCH_SEARCH_REQUEST_FINISHED,
      data,
      newbuildingInfo: null,
      developerInfo,
      villageInfo,
      similarNewobjects,
      similarNewobjectsFromDeveloper,
      quickLinks,
      recommendedVillages,
      suburbanBuildersProjects,
      newbuildingLayouts: null,
    });
    dispatch(incrementAnimationCounter());

    state = getState();

    const consultantPhone = config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions = config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];
    displayConsultantPhone(state, consultantPhoneEnabledRegions, consultantPhone);

    trackPageView(
      {
        jsonQueryFromFilters: jsonQuery,
        label: searchMeta.fullUrl,
        results: state.results,
        breadcrumbs: data.breadcrumbs,
        user: state.user,
        profileSessionKey: state.profileSessionKey,
        mlRankingGuid: data.mlRankingGuid || null,
        mlRankingModelVersion: data.mlRankingModelVersion || null,
        searchRequestId: data.searchRequestId,
        extensions: data.extensionTypes || undefined,
        location: state.currentLocation,
      },
      config,
    );
  };
}

function areDatesChanged(prevJsonQuery: TJsonQuery, nextJsonQuery: TJsonQuery) {
  const { gte: resultsDateFrom, lt: resultsDateTo } = getDateRangeValue('dates')(prevJsonQuery) || {};
  const { gte: filtersDateFrom, lt: filtersDateTo } = getDateRangeValue('dates')(nextJsonQuery) || {};

  return resultsDateFrom !== filtersDateFrom || resultsDateTo !== filtersDateTo;
}
